import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"
import { RootState } from "./store"

export const PERMISSIONS_TTL_MS = 45 * 60 * 1000 // 45 minutes

export type PermissionsState = {
  permissions: string[]
  /** Someone on this account holds the fleet manager role, so vehicles are
   * handed out by them and dispatchers no longer join or leave chats
   * themselves. */
  accountManaged: boolean
  fetchedAt: number | null
}

const initialState: PermissionsState = {
  permissions: [],
  accountManaged: false,
  fetchedAt: null,
}

export const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    setPermissions: (
      state,
      action: PayloadAction<{
        permissions: string[]
        accountManaged?: boolean
      }>,
    ) => {
      state.permissions = action.payload.permissions
      state.accountManaged = action.payload.accountManaged ?? false
      state.fetchedAt = Date.now()
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const { setPermissions } = permissionsSlice.actions

export const VIEW_DISPATCHERS = "dispatchers.view"
export const ASSIGN_DISPATCHERS = "dispatchers.assign"

export const selectPermissions = (state: RootState) => state.permissions

/** Sees every dispatcher's chats and hands vehicles out between them. */
export const selectIsFleetManager = (state: RootState) =>
  state.permissions.permissions.includes(VIEW_DISPATCHERS)

export const selectPermissionsStale = (state: RootState): boolean => {
  const { fetchedAt } = state.permissions
  if (fetchedAt === null) return true
  return Date.now() - fetchedAt > PERMISSIONS_TTL_MS
}

export default permissionsSlice.reducer
